import type { ReactNode } from 'react';

import type { MapWorkbenchRuntimeSurfaceSectionRenderArgs } from '@workspace/ui';

export type RuntimeSurfaceSectionRenderer = (
  args: MapWorkbenchRuntimeSurfaceSectionRenderArgs,
) => ReactNode;

export type RuntimeSurfaceComponentRenderer = (
  args: MapWorkbenchRuntimeSurfaceSectionRenderArgs,
) => ReactNode;

const readString = (value: unknown) => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

export const readRuntimeSurfaceSectionComponentId = (
  section: MapWorkbenchRuntimeSurfaceSectionRenderArgs['section'],
) => {
  const binding = section.binding && typeof section.binding === 'object'
    ? (section.binding as Record<string, unknown>)
    : null;
  const bindingId = readString(binding?.component_id);
  if (bindingId) {
    return bindingId;
  }
  if (!Array.isArray(section.items)) {
    return null;
  }
  for (const item of section.items) {
    const itemId =
      item && typeof item === 'object'
        ? readString((item as Record<string, unknown>).component_id)
        : null;
    if (itemId) {
      return itemId;
    }
  }
  return null;
};

export const createRuntimeSurfaceComponentRenderer = (
  registry: Record<string, RuntimeSurfaceComponentRenderer>,
): RuntimeSurfaceSectionRenderer => {
  return args => {
    if (args.section.kind !== 'custom') {
      return null;
    }
    const componentId = readRuntimeSurfaceSectionComponentId(args.section);
    if (!componentId) {
      return null;
    }
    const renderer = registry[componentId];
    return renderer ? renderer(args) : null;
  };
};

export const composeRuntimeSurfaceSectionRenderers = (
  ...renderers: RuntimeSurfaceSectionRenderer[]
): RuntimeSurfaceSectionRenderer => {
  return args => {
    for (const renderer of renderers) {
      const rendered = renderer(args);
      if (rendered) {
        return rendered;
      }
    }
    return null;
  };
};
